import {
  Text,
  Button,
  Modal,
  InputSelectControlField,
  TextInputField,
  Checkbox,
  Label,
  Primary13Regular,
  Primary13SemiBold,
  Banner,
  RedRed60,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useState, useMemo, useCallback } from 'react'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { AnrokTaxCategory } from 'SettingsV2/components/tax-rates/taxRates.types'
import { getApiErrorMessage } from 'Cube/utils/getApiErrorMessage'

interface Props {
  taxCategoryName: string
  currentAnrokTaxCategoryId: string
  currentAnrokTaxCategoryType: 'standard' | 'custom'
  isDefault: boolean
  onUpdateMapping: (values: {
    anrokTaxCategoryId: string
    anrokTaxCategoryType: 'standard' | 'custom'
    isDefault: boolean
  }) => Promise<void>
  onClose: () => void
}

const EditAnrokMappingModalUI = ({
  taxCategoryName,
  currentAnrokTaxCategoryId,
  currentAnrokTaxCategoryType,
  isDefault,
  onUpdateMapping,
  onClose
}: Omit<Props, 'trigger'> & { onClose: () => void }) => {
  const initialSelectionValue = JSON.stringify({
    type: currentAnrokTaxCategoryType,
    id: currentAnrokTaxCategoryId
  })

  const { fields, queries } = useForm({
    value: {
      anrokTaxCategory: initialSelectionValue,
      isDefault: isDefault
    },
    fieldConfiguration: [
      {
        property: 'anrokTaxCategory',
        validation: [required]
      },
      {
        property: 'isDefault',
        validation: []
      }
    ]
  })

  const {
    data: anrokTaxCategoriesData,
    isLoading: isLoadingCategories,
    isError: hasError
  } = useQuery(dashboardv99990101Client.getAnrokTaxCategories)

  const anrokTaxCategories = anrokTaxCategoriesData?.items || []

  const comboBoxOptions = useMemo(
    () =>
      anrokTaxCategories.map((category: AnrokTaxCategory) => ({
        value: JSON.stringify({ type: category.type, id: category.id }),
        label: category.name
      })),
    [anrokTaxCategories]
  )

  const [selectedValue, setSelectedValue] = useState<string>(
    initialSelectionValue
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const hasChanged =
    selectedValue !== initialSelectionValue ||
    fields.isDefault.value !== isDefault

  const canSubmit =
    queries.isValid && hasChanged && !isSubmitting && !isLoadingCategories

  const handleChange = useCallback(
    (value: string) => {
      setSelectedValue(value)
      fields.anrokTaxCategory.onChange(value)
    },
    [fields.anrokTaxCategory]
  )

  const handleSubmit = useCallback(async () => {
    if (selectedValue && !isSubmitting && queries.isValid) {
      setIsSubmitting(true)
      setSubmitError(null)
      try {
        const categoryId = JSON.parse(selectedValue) as {
          type: 'standard' | 'custom'
          id: string
        }
        await onUpdateMapping({
          anrokTaxCategoryId: categoryId.id,
          anrokTaxCategoryType: categoryId.type,
          isDefault: fields.isDefault.value
        })
        onClose()
      } catch (error) {
        console.error('Failed to update Anrok mapping:', error)
        setSubmitError(
          getApiErrorMessage(error, 'Failed to update Anrok tax code mapping')
        )
      } finally {
        setIsSubmitting(false)
      }
    }
  }, [
    selectedValue,
    isSubmitting,
    queries.isValid,
    fields.isDefault.value,
    onUpdateMapping,
    onClose
  ])

  return (
    <>
      <Modal.Header showCloseButton withBorder onClose={onClose}>
        Edit Anrok mapping
      </Modal.Header>
      <Modal.Content minHeight="auto">
        <Flex flexDirection="column" gap={3}>
          {submitError && (
            <Banner variant="error" data-testid="editAnrokMapping.submitError">
              {submitError}
            </Banner>
          )}
          <Flex flexDirection="column" gap={2}>
            <Text {...Primary13Regular}>Mapping for:</Text>
            <Text as="span" {...Primary13SemiBold}>
              {taxCategoryName}
            </Text>
          </Flex>

          {hasError && (
            <Text
              color={RedRed60}
              {...Primary13Regular}
              data-testid="editAnrokMapping.error"
            >
              Failed to load Anrok tax categories. Please try again.
            </Text>
          )}

          <InputSelectControlField
            label="Anrok tax category"
            placeholder={
              isLoadingCategories
                ? 'Loading categories...'
                : 'Select a tax category...'
            }
            options={comboBoxOptions}
            initialValue={selectedValue}
            onChange={handleChange}
            disabled={isLoadingCategories}
            data-testid="anrokTaxCategoryComboBox"
          />

          <Checkbox
            isChecked={fields.isDefault.value}
            onChange={e => fields.isDefault.onChange(e.target.checked)}
            label="Mark as default"
            data-testid="editAnrokMapping.isDefault"
          />
        </Flex>
      </Modal.Content>
      <Modal.Footer withBorder>
        <Button
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
          variant="secondary"
          disabled={isSubmitting}
          data-testid="editAnrokMapping.cancel"
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-testid="editAnrokMapping.submit"
        >
          {isSubmitting ? 'Updating...' : 'Update mapping'}
        </Button>
      </Modal.Footer>
    </>
  )
}

const EditAnrokMappingModal = ({ onClose, ...props }: Props) => (
  <Modal isOpen onClose={onClose} data-testid="editAnrokMappingModal">
    <EditAnrokMappingModalUI {...props} onClose={onClose} />
  </Modal>
)

export default EditAnrokMappingModal
